<script lang="ts">
    import { onNavigate, spaLink } from "../../../../functionTools/routing"
    import { lang, location, subNavs } from "../../../../store"
    import MediaQuery from "../../../widgets/MediaQuery.svelte"
    import HeaderModal from "./HeaderModal.svelte"
    import ImageCard from "./ImageCard.svelte"
    import LanguageSelect from "./LanguageSelect.svelte"
    import NavigationCard from "./NavigationCard.svelte"
    import NavigationTopicCards from "./NavigationTopicCards.svelte"
    import SearchSelect from "./SearchSelect.svelte"
    import { kirbyFetch } from "../../../../api/kirby/api"
    import { initialLocale } from "../../../../i18n"
    import { onMount } from "svelte"
    import PagerefLink from "../../../widgets/PagerefLink.svelte"

    let navSections: NavSectionContent[] = $state([])
    const forceDirectLinkTabs = ["veranstaltungen", "events", "positionen", "positions"]
    const shouldForceDirectLink = (section: NavSectionContent) => {
        const tabName = section.tab_name?.trim().toLowerCase()
        const articleTypes = (section.article_types || "")
            .split(",")
            .map((type) => type.trim().toLowerCase())
            .filter(Boolean)
        return (
            forceDirectLinkTabs.includes(tabName) || articleTypes.includes("event") || articleTypes.includes("position")
        )
    }

    kirbyFetch<Site>({
        query: "site",
        select: {
            main_nav: true,
        },
    }).then(async (res) => {
        const normalizedNavs = res.main_nav
            .map((el) => el.content)
            .map((section) => (shouldForceDirectLink(section) ? { ...section, nav_type: "direct_link" } : section))

        navSections = normalizedNavs
        let _subNavs = []
        for (const nav of normalizedNavs) {
            if (nav.nav_type == "subnavigation_based") {
                const subPageIds = [...nav.subnavigation_based.map((el) => el.link), nav.primary_link]
                _subNavs.push([subPageIds, nav])
            }
        }
        //@ts-ignore
        subNavs.set(_subNavs)
    })

    let {
        type,
        position = "fixed",
    }: {
        type: "white" | "transparent"
        position?: "sticky" | "fixed"
    } = $props()

    let visibleModal = $state(-1)
    $effect(() => {
        if (typeof window === "undefined") return
        if (!showMobileMenu) {
            if (visibleModal !== -1) document.body.style.overflow = "hidden"
            else document.body.style.overflow = "auto"
        }
    })
    let showMobileMenu = $state(false)
    let modal: HTMLDialogElement | null = null
    $effect(() => {
        if (typeof window === "undefined") return
        visibleModal = -1
        showMobileMenu = false
    })
    $effect(() => {
        if (typeof window === "undefined") return
        if (showMobileMenu && !!modal) {
            document.body.style.overflow = "hidden"
            modal?.showModal()
            requestAnimationFrame(() => modal && modal.classList.add("visibleC"))
        } else if (!!modal) {
            modal.classList.remove("visibleC")
            const handleTransitionEnd = (event: TransitionEvent) => {
                if (event.propertyName === "top" || event.propertyName === "opacity") {
                    modal.removeEventListener("transitionend", handleTransitionEnd)
                    modal.close()
                    document.body.style.overflow = "auto"
                }
            }
            modal.addEventListener("transitionend", handleTransitionEnd)
            document.body.style.overflow = "auto"
        }
    })
    onMount(() => {
        onNavigate(() => {
            // close everything
            visibleModal = -1
            showMobileMenu = false
            if (modal) {
                modal.classList.remove("visibleC")
                modal.close()
                document.body.style.overflow = "auto"
            }
        })
    })
</script>

{#snippet Burger(force: boolean)}
    {#if !showMobileMenu || force}
        <button
            class="burger"
            onclick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                showMobileMenu = !showMobileMenu
            }}
        >
            {#if !showMobileMenu}
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M20 7H4M20 12H4M20 17H4"
                        stroke={type == "transparent" ? "white" : "#242220"}
                        stroke-width="1.5"
                        stroke-linecap="round"
                    ></path>
                </svg>
            {:else}
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M6.7583 17.2426L12.0009 12M12.0009 12L17.2435 6.75735M12.0009 12L6.7583 6.75735M12.0009 12L17.2435 17.2426"
                        stroke={type == "transparent" ? "white" : "#242220"}
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    ></path>
                </svg>
            {/if}
        </button>
    {/if}
{/snippet}
{#snippet HeaderWrapper()}
    <div class="wrapper">
        <a href="/{$lang || initialLocale}" use:spaLink style="cursor: pointer;">
            <img
                src="/logo/{type == 'white' ? 'dark' : 'white'}{($lang || initialLocale).startsWith('de')
                    ? ''
                    : '_en'}.svg"
                alt="Logo"
            />
        </a>
        <MediaQuery query={"(min-width: 1500px)"}>
            {#snippet contentSnippet()}
                <nav>
                    <ul>
                        {#each navSections as section, idx}
                            <PagerefLink
                                {type}
                                isButton={section.nav_type !== "direct_link"}
                                text={section.tab_name}
                                page_ref={section.primary_link}
                                setModal={() => (visibleModal = idx)}
                            />

                            {#if section.nav_type == "subnavigation_based" && visibleModal === idx}
                                <HeaderModal show={visibleModal === idx} closeModal={() => (visibleModal = -1)}>
                                    {#snippet imagePreview()}
                                        <ImageCard {section} />
                                    {/snippet}
                                    {#snippet navigation()}
                                        <NavigationCard items={section.subnavigation_based} />
                                    {/snippet}
                                </HeaderModal>
                            {:else if section.nav_type == "topic_based" && visibleModal === idx}
                                <HeaderModal
                                    show={visibleModal === idx}
                                    closeModal={() => (visibleModal = -1)}
                                    extendedHeight={true}
                                >
                                    {#snippet navigation()}
                                        <NavigationTopicCards cards={section.topic_based} {section} />
                                    {/snippet}
                                </HeaderModal>
                            {/if}
                        {/each}
                    </ul>
                </nav>
            {/snippet}
        </MediaQuery>
        <div class="toolbar">
            {#if !showMobileMenu}
                <div class="search-select">
                    <SearchSelect color={type == "white" ? "dark" : "bright"} />
                </div>
                <div class="language-select">
                    <LanguageSelect color={type == "white" ? "dark" : "bright"} />
                </div>
                {@render Burger(false)}
            {/if}
        </div>
    </div>
{/snippet}
<header id="header" class="type-{type} pos-{position}">
    <div class="outer-wrapper">
        {@render HeaderWrapper()}
    </div>
    <div class="border-bar"></div>
    <MediaQuery query={"(max-width: 1499px)"}>
        {#snippet contentSnippet()}
            <dialog class="mobile-nav" class:visible={showMobileMenu} bind:this={modal}>
                <div class="burger-wrapper">
                    {@render Burger(true)}
                </div>
                <ul class="main-nav-ul">
                    {#each navSections as section, idx}
                        <li>
                            {#if section.nav_type === "direct_link"}
                                <PagerefLink
                                    {type}
                                    isButton={false}
                                    text={section.tab_name}
                                    page_ref={section.primary_link}
                                    setModal={() => (visibleModal = -1)}
                                />
                            {:else}
                                <button
                                    class="nav-titles"
                                    class:active={visibleModal == idx}
                                    onclick={() => {
                                        if (visibleModal === idx) visibleModal = -1
                                        else visibleModal = idx
                                    }}
                                >
                                    <span>{section.tab_name}</span>
                                    <span>
                                        <img src="media/chevron-down.svg" alt="chevron-down" />
                                    </span>
                                </button>
                                {#if section.nav_type == "subnavigation_based" && visibleModal === idx}
                                    <div class="content">
                                        <ImageCard {section} />
                                        <NavigationCard items={section.subnavigation_based} />
                                    </div>
                                {:else if section.nav_type == "topic_based" && visibleModal === idx}
                                    <div class="content">
                                        <NavigationTopicCards cards={section.topic_based} {section} />
                                    </div>
                                {/if}
                            {/if}
                        </li>
                    {/each}
                </ul>
            </dialog>
        {/snippet}
    </MediaQuery>
</header>

<style lang="less" global>
    @import "../../../../css/variables.less";
    #header {
        width: 100%;
        display: flex;
        align-items: center;
        flex-direction: column;
        overflow: hidden;
        height: var(--header-height);
        .burger {
            border-radius: 50px;
            background: rgba(255, 255, 255, 0.01);
            position: relative;
            height: 2.4rem;
            width: 2.4rem;
            display: flex;
            justify-content: center;
            align-items: center;
            display: none;
        }
        .nav-titles {
            position: relative;

            .isActiveDot {
                position: absolute;

                bottom: -1.5rem;
                right: 50%;
                transform: translateX(50%);
            }
        }
        dialog.mobile-nav {
            height: 100vh;
            min-height: 100vh;
            width: 100%;
            min-width: 100%;
            top: -100vh;
            left: 0px;
            overflow: hidden;
            transition:
                top 0.5s ease,
                opacity 0.5s ease,
                visibility 0.5s ease;
            opacity: 0;

            z-index: 9998;
            position: fixed;
            display: flex;
            flex-direction: column;
            &.visibleC {
                top: 0px;
                opacity: 1;
            }

            .burger-wrapper {
                min-height: 102px;
                display: flex;
                align-items: flex-start;

                justify-content: flex-end;
                padding: 0px var(--default-padding);
                .burger {
                    display: flex;
                    margin-top: 0.6rem;
                }
            }

            & > ul.main-nav-ul {
                background-color: var(--bright-100);
                flex-grow: 1;
                display: flex;
                border-radius: 1.2rem 1.2rem 0px 0px;
                flex-direction: column;
                padding: var(--default-padding);
                gap: 0.5rem;
                overflow: auto;
                .nav-titles {
                    display: flex;
                    width: 100%;
                    justify-content: space-between;
                    align-items: center;
                    &.active {
                        img {
                            transform: rotate(180deg);
                        }
                    }
                }
                & > li {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                    padding-bottom: 6px;
                    border-bottom: 1px solid var(--dark-20);
                    &:last-child {
                        border-bottom: none;
                    }
                    & > a.nav-titles,
                    & > button {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        width: 100%;
                        font-size: 1.2rem;
                        & > span,
                        & > div {
                            font-size: 1.2rem;
                            display: flex;
                            align-items: center;
                            gap: 0.5rem;
                        }
                        img {
                            transform: rotate(0deg);
                            transition: transform 0.3s;
                        }
                        &.active {
                            img {
                                transform: rotate(180deg);
                            }
                        }
                    }
                    & > .content {
                        display: flex;
                        flex-direction: column;
                        gap: 1rem;
                    }
                }
            }
        }
        .outer-wrapper {
            width: 100%;
            display: flex;
            justify-content: center;
            height: 108px;
            .wrapper {
                display: flex;
                margin: 0px var(--default-padding);
                max-width: var(--extended-max-width);
                width: 100%;
                height: 100%;
                justify-content: space-between;
                height: 100%;
                align-items: flex-start;

                & > nav {
                    height: 72px;
                    display: flex;
                    align-items: center;
                    & > ul {
                        display: flex;
                        justify-content: center;
                        align-items: flex-end;
                        height: 100%;
                        gap: 1.8rem;
                        .nav-titles {
                            font-size: 1rem;
                            color: var(--bright-100);
                        }
                    }
                }
                .toolbar {
                    display: flex;
                    height: 72px;
                    align-items: flex-end;
                    gap: 0.5rem;
                }

                @media @max-mobile-header {
                    position: relative;
                    .toolbar {
                        .burger {
                            display: flex;
                        }
                        height: fit-content;
                        margin-top: 0.6rem;
                    }
                    justify-content: flex-end;
                    a {
                        position: absolute;
                        left: 0px;
                        top: 0px;
                        bottom: 1.2rem;
                        width: 100%;

                        img {
                            height: 100% !important;
                            max-width: 100% !important;
                            object-position: top center !important;
                            object-fit: contain !important;
                        }
                    }
                }
            }
        }
        .border-bar {
            height: 18px;
            position: relative;
            width: 100%;
            border-top: 1px solid white;
            border-radius: 20px 20px 0 0;
            background-color: transparent;
        }
        .border-bar::before {
            content: "";
            position: absolute;
            top: -5px; /* Adjust based on blur size */
            left: -6px;
            height: 12px;
            width: 13px;
            transform: rotate(-45deg);
            border-radius: 0px;
            background: #d9d9d923;
            backdrop-filter: blur(5px); /* Apply blur */
            z-index: 1; /* Push behind the main element */
            border-radius: inherit; /* Match border radius of the parent */
        }
        .border-bar::after {
            content: "";
            position: absolute;
            top: -5px; /* Adjust based on blur size */
            right: -6px;
            height: 12px;
            width: 13px;
            transform: rotate(45deg);
            border-radius: 0px;
            background: #d9d9d923;
            backdrop-filter: blur(2px); /* Apply blur */
            z-index: 1; /* Push behind the main element */
            border-radius: inherit; /* Match border radius of the parent */
        }
        position: fixed;
        top: 0px;
        z-index: 100;
        &.type-transparent {
            .outer-wrapper {
                background: #d9d9d923;
                backdrop-filter: blur(5px);
                -webkit-backdrop-filter: blur(5px);
            }
        }
        &.type-white {
            .nav-titles {
                color: var(--dark-100) !important;
            }
            .outer-wrapper {
                background-color: var(--bright-100);
                box-shadow: 0px 0px 30px 0px rgba(0, 0, 0, 0.1);
            }
            .border-bar {
                border-top-color: var(--bright-100);
            }
            .border-bar::before {
                background: var(--bright-100) !important;
                top: -6px; /* Adjust based on blur size */
                left: -6px;
                height: 13px;
                width: 16px;

                backdrop-filter: initial;
            }
            .border-bar::after {
                top: -6px; /* Adjust based on blur size */
                right: -6px;
                height: 12px;
                width: 19px;
                background: var(--bright-100) !important;

                backdrop-filter: initial;
            }
        }
        &.pos-sticky {
            position: sticky;
            top: 0px;
        }
    }
</style>
